// L1 — „Vorgänge" als eigene LISTENQUELLE.
//
// Der Filter „Vorgänge" der Mail-Übersicht sieht nicht mehr die geladene
// Ordnerseite durch, sondern zeigt die verknüpften Mails selbst. Die kommen aus
// `GET /mail/links/verknuepft` als SNAPSHOT (Betreff, Absender, „An", Datum,
// Vorschau) — ohne Body und ohne Anhänge. Diese Datei übersetzt einen solchen
// Snapshot in die `Email`-Form, mit der die Liste ohnehin arbeitet, damit die
// Zeile identisch aussieht, egal woher sie stammt.
//
// Der fehlende Body ist Absicht und wird über `snapshotOnly` markiert: Öffnen
// lädt die Mail über `fetchMessageBody` am Quell-Postfach nach, wo die reguläre
// Mail-Berechtigung greift. Ohne diese Markierung landete beim Klick eine Mail
// mit leerem Rumpf im Lesebereich.
import { getDateLabel, stringToColor, type Email } from "./graphMailService";
import type { VerknuepfteMailItemDto } from "./mailLinkService";

/**
 * Eine aus einem Verknüpfungs-Snapshot gebaute Listenzeile.
 *
 * `srcMailbox` = das Postfach, gegen das gelesen wird (die Snapshots tragen es
 * mit); `snapshotOnly` = „Rumpf fehlt, vor dem Anzeigen nachladen";
 * `vorgang` = der Vorgang, an dem die Mail hängt (für die Anzeige am Eintrag).
 */
export type VorgangsMail = Email & {
    srcMailbox: string;
    snapshotOnly: true;
    vorgang: { id: number; titel: string } | null;
};

/** Snapshot → Listenzeile. Rein, ohne Netzzugriff. */
export function snapshotZuEmail(item: VerknuepfteMailItemDto): VorgangsMail {
    const senderName = item.senderName || item.senderEmail || "Unbekannt";
    // Fehlt der Zeitstempel (sehr alte Verknüpfung ohne Snapshot), lieber ein
    // ungültiges Datum als „heute": ein erfundenes Datum würde in der
    // Zeitraum-Gruppierung schlicht falsch einsortiert.
    const date = item.receivedAt ? new Date(item.receivedAt) : new Date(NaN);
    const gueltig = !Number.isNaN(date.getTime());
    return {
        id: item.messageId,
        conversationId: item.conversationId || item.messageId,
        senderName,
        senderEmail: item.senderEmail || "",
        senderInitial: senderName.charAt(0).toUpperCase() || "?",
        senderColor: stringToColor(senderName),
        subject: item.subject || "Kein Betreff",
        date,
        dateLabel: gueltig ? getDateLabel(date) : "",
        preview: item.preview || "",
        body: "",
        type: "normal",
        // Der Snapshot kennt den Gelesen-Status nicht. „gelesen" ist die
        // ehrlichere Annahme als ein blauer Punkt an jeder Zeile.
        read: true,
        to: [],
        cc: [],
        srcMailbox: item.mailbox,
        snapshotOnly: true,
        vorgang: item.vorgang,
    };
}

/**
 * Volltext-Abgleich für die Suche INNERHALB der Vorgangs-Mails.
 *
 * Solange der Filter „Vorgänge" die Listenquelle stellt, kann die globale
 * Server-Suche nicht greifen (sie sucht über Postfächer, nicht über
 * Verknüpfungen). Statt die Eingabe wirkungslos verpuffen zu lassen, wird sie
 * lokal auf die geladenen Vorgangs-Mails angewandt — über Betreff, Absender,
 * Vorschau und Vorgangstitel, also über alles, was der Snapshot hergibt.
 */
export function matchesVorgangsMailSuche(mail: VorgangsMail, suchtext: string): boolean {
    const needle = suchtext.trim().toLowerCase();
    if (!needle) return true;
    return [mail.subject, mail.senderName, mail.senderEmail, mail.preview, mail.vorgang?.titel]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
}
